import { memo, useState } from 'react';
import { domainOf, hueOf, initials, isFreemail } from '@dts/core';
import { useUi } from '@/lib/store';
import { cn } from './ui';

const failed = new Set<string>();

/** The browser loads favicons straight from the domain's site; desktop discovers them in its main process. */
const faviconSrc = (domain: string) => `https://${domain}/favicon.ico`;

/** Domain favicon fetched from the sender's own website. */
export const Favicon = memo(function Favicon({ domain, size = 16, className }: { domain: string; size?: number; className?: string }) {
  const enabled = useUi((s) => s.local.favicons);
  const [broken, setBroken] = useState(failed.has(domain));
  if (!enabled || !domain || broken) {
    return (
      <span
        className={cn('inline-flex shrink-0 items-center justify-center rounded-[5px] text-[9px] font-bold text-white', className)}
        style={{ width: size, height: size, background: `oklch(var(--av-l1) var(--av-c1) ${hueOf(domain)})` }}
      >
        {domain.charAt(0).toUpperCase()}
      </span>
    );
  }
  return (
    <img
      src={faviconSrc(domain)}
      referrerPolicy="no-referrer"
      alt=""
      draggable={false}
      width={size}
      height={size}
      className={cn('shrink-0 rounded-[4px] object-contain', className)}
      onError={() => {
        failed.add(domain);
        setBroken(true);
      }}
    />
  );
});

/**
 * Sender avatar: a squircle showing the company favicon for business domains, or a gradient monogram for
 * consumer mailboxes (gmail, outlook...) and domains without an icon.
 */
export const Avatar = memo(function Avatar({ name, address, size = 36, className, ring }: { name: string; address: string; size?: number; className?: string; ring?: boolean }) {
  const enabled = useUi((s) => s.local.favicons);
  const domain = domainOf(address);
  const [broken, setBroken] = useState(failed.has(domain));
  const useIcon = enabled && domain && !isFreemail(domain) && !broken;
  const hue = hueOf(address || name);
  const radius = size * 0.32;
  return (
    <div
      className={cn('relative flex shrink-0 items-center justify-center overflow-hidden', ring && 'ring-2 ring-accent/40', className)}
      style={{
        width: size,
        height: size,
        borderRadius: radius,
        background: useIcon
          ? 'var(--raised)'
          : `linear-gradient(135deg, oklch(var(--av-l1) var(--av-c1) ${hue}), oklch(var(--av-l2) var(--av-c2) ${(hue + 50) % 360}))`, // muted inks in the light Dossier
        boxShadow: useIcon ? 'inset 0 0 0 1px var(--line-strong)' : 'inset 0 1px 0 rgba(255,255,255,.25)',
      }}
    >
      {useIcon ? (
        <img
          src={faviconSrc(domain)}
          referrerPolicy="no-referrer"
          alt=""
          draggable={false}
          style={{ width: size * 0.6, height: size * 0.6 }}
          className="object-contain"
          onError={() => {
            failed.add(domain);
            setBroken(true);
          }}
        />
      ) : (
        <span className="font-semibold text-white" style={{ fontSize: size * 0.38, letterSpacing: '-0.02em' }}>
          {initials(name || address)}
        </span>
      )}
    </div>
  );
});
